"use client";

import styled, { CSSProperties } from "styled-components";
import { Icon } from "../Icon/Icon";
import CaseDataLoader from "../CaseDataLoader/CaseDataLoader";

interface IButton {
  label?: string;
  icon?: string;
  iconColor?: string;
  onPress: () => void;
  name: string;
  theme: string;
  isDisabled?: boolean;
  styles?: CSSProperties;
  labelStyles?: CSSProperties;
  large?: boolean;
  loaderSize?: string;
  small?: boolean;
}

const JNJButton = styled.button<{ $small?: boolean }>`
  display: flex;
  padding: ${(props) => (props.$small ? "3px 10px" : "5px 20px")};
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: 1px solid transparent;
  border-radius: 100px;
  cursor: pointer;
  transition: all 300ms ease-out;

  &.primary {
    color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.main};

    .icon-button {
      filter: brightness(0) invert(1);
    }

    &:hover {
      background-color: ${(props) => props.theme.primary.hovered};
    }
  }

  &.secondary {
    color: ${(props) => props.theme.main};
    border-color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.white};

    &:hover {
      background-color: ${(props) => props.theme.secondary.hovered};
      border-color: ${(props) => props.theme.main};
    }
  }

  &.tertiary {
    border-color: ${(props) => props.theme.main};
    color: ${(props) => props.theme.black};
    background-color: ${(props) => props.theme.white};

    .icon-button {
      filter: brightness(0);
    }

    &:hover {
      background-color: ${(props) => props.theme.main};
    }
  }

  &.confirm {
    border-color: ${(props) => props.theme.green};
    color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.green};

    &:hover {
      background-color: ${(props) => props.theme.greenHover};
      border-color: ${(props) => props.theme.green};
    }

    &:disabled {
      opacity: 1;
      background-color: ${(props) => props.theme.greenHover};
      border-color: ${(props) => props.theme.greenHover};
    }
  }

  &.toggle-email {
    border-color: ${(props) => props.theme.incision};
    color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.incision};

    &:hover {
      opacity: 0.5;
    }
  }

  &.toggle-sms {
    border-color: ${(props) => props.theme.sms};
    color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.sms};

    &:hover {
      opacity: 0.5;
    }
  }

  &.toggle-off {
    border-color: ${(props) => props.theme.off};
    color: ${(props) => props.theme.white};
    background-color: ${(props) => props.theme.off};

    &:hover {
      opacity: 0.5;
    }
  }

  p {
    font-size: 1rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export default function Button({
  label,
  icon,
  iconColor,
  onPress,
  name,
  theme,
  isDisabled,
  styles,
  labelStyles,
  large,
  loaderSize,
  small,
}: IButton) {
  return (
    <JNJButton
      disabled={isDisabled}
      name={name}
      onClick={(e) => {
        e.stopPropagation();
        onPress();
      }}
      className={`${theme} ${name}`}
      style={{ ...styles }}
      $small={small}
    >
      {icon === "loader" ? (
        <CaseDataLoader
          percent={null}
          size={loaderSize || "20px"}
          color={iconColor}
        />
      ) : icon ? (
        <Icon
          url={icon}
          color={iconColor ? iconColor : "#EB1700"}
          customClass="icon-button"
          height={large ? "20px" : small ? "12px" : "17px"}
          width={large ? "20px" : small ? "12px" : "17px"}
        />
      ) : null}
      {label && (
        <p
          className="johnson-text"
          style={{
            ...(large
              ? { fontSize: "1.5rem" }
              : small
                ? { fontSize: "0.9rem" }
                : {}),
            ...labelStyles,
          }}
        >
          {label}
        </p>
      )}
    </JNJButton>
  );
}
